import React, { useState } from 'react';
import { X, Edit, Save, Calendar, Users, Trash2, GitBranch, AlertTriangle, Shield } from 'lucide-react';
import SearchableSelect from '../ui/SearchableSelect';
import api from '../../services/api';
import { downloadItinerary } from '../../services/itinerary';
import { itineraryVersionService } from '../../services/itineraryVersionService';
import { useAuth } from '../../contexts/AuthContext';

interface EnquiryDetailsModalProps {
  isOpen?: boolean;
  onClose: () => void;
  enquiry: any;
  onReworkCreated?: () => void;
  agencies?: any[];
  onSave?: (updatedData: any) => void;
}

const EnquiryDetailsModal: React.FC<EnquiryDetailsModalProps> = ({
  isOpen,
  onClose,
  enquiry,
  onReworkCreated,
  agencies = [],
  onSave
}) => {
  const { isAdmin, isTeamLead } = useAuth();
  const [showReworkModal, setShowReworkModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<{requestId: number, hasItineraries: boolean, itineraryNames: string[]} | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedItinerary, setSelectedItinerary] = useState<{id: number, name: string} | null>(null);
  const [detailedEnquiry, setDetailedEnquiry] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingRequestId, setEditingRequestId] = useState<number | null>(null);
  const [editData, setEditData] = useState<any>({});
  const [creatingVersionFor, setCreatingVersionFor] = useState<number | null>(null);
  const [reworkForm, setReworkForm] = useState({
    paxChanged: false,
    nightsChanged: false,
    dateChanged: false,
    changeReason: '',
    newAdults: '',
    newKids: '',
    newNights: '',
    newTravelDate: '',
    attachment: null as File | null
  });

  // Reset editing state when modal opens/closes or enquiry changes
  React.useEffect(() => {
    if (isOpen === false) {
      setIsEditing(false);
      setEditingRequestId(null);
      setEditData({});
    }
  }, [isOpen]);

  React.useEffect(() => {
    setIsEditing(false);
    setEditingRequestId(null);
    setEditData({});
  }, [enquiry?.id, enquiry?.enquiry_id]);

  React.useEffect(() => {
    if (isOpen !== false || showReworkModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, showReworkModal]);

  // Fetch detailed enquiry data when modal opens
  React.useEffect(() => {
    if (isOpen !== false && enquiry && !enquiry.request_history) {
      fetchDetailedEnquiry();
    } else if (enquiry?.request_history) {
      setDetailedEnquiry(enquiry);
    }
  }, [isOpen, enquiry]);

  const handleCreateVersion = async (baseItineraryId: number) => {
    if (!window.confirm(`Create a new version from WH_${baseItineraryId}? This will create a new itinerary under the same quotation.`)) return;
    try {
      setCreatingVersionFor(baseItineraryId);
      const res = await itineraryVersionService.createVersion(baseItineraryId);
      if (res.data.success) {
        alert(`✅ Version ${res.data.version_number} created: ${res.data.new_wyn_itinerary_id || 'WH_' + res.data.new_itinerary_id} under ${res.data.quotation_number}\n\nNow map this itinerary to the rework request.`);
      }
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to create version');
    } finally {
      setCreatingVersionFor(null);
    }
  };

  const fetchDetailedEnquiry = async () => {
    try {
      setLoading(true);
      const enquiryId = enquiry.enquiry_id || enquiry.id;
      const response = await api.get(`/enquiry-details/${enquiryId}`);
      if (response.data.success) {
        setDetailedEnquiry(response.data.data);
      } else {
        console.error('Failed to fetch detailed enquiry:', response.data);
        setDetailedEnquiry(enquiry); // Fallback to original data
      }
    } catch (error) {
      console.error('Error fetching detailed enquiry:', error);
      setDetailedEnquiry(enquiry); // Fallback to original data
    } finally {
      setLoading(false);
    }
  };

  if (isOpen === false || !enquiry) return null;

  const displayEnquiry = detailedEnquiry || enquiry;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-hidden">
        <div className="bg-white rounded-xl p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading enquiry details...</p>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'assigned': return 'bg-purple-100 text-purple-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'in_progress': return 'In Progress';
      case 'completed': return 'Completed';
      case 'pending': return 'Pending';
      case 'assigned': return 'Assigned';
      default: return status;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-green-100 text-green-800';
    }
  };

  const handleEditRequest = (request: any) => {
    setEditingRequestId(request.id);
    setEditData({
      agency_id: agencies.find(a => a.agency_name === displayEnquiry.agency_name)?.agency_id || '',
      travel_date: request.travel_date || displayEnquiry.travel_date || '',
      number_of_adults: request.number_of_adults || displayEnquiry.number_of_adults || 2,
      request_text: request.change_reason || request.request_text || ''
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingRequestId(null);
    setEditData({});
  };

  const handleSave = async () => {
    try {
      const enquiryId = enquiry.enquiry_id || enquiry.id;
      await api.put(`/v2/enquiries/${enquiryId}/requests/${editingRequestId}`, editData);
      alert('Request updated successfully!');
      setIsEditing(false);
      setEditingRequestId(null);
      setEditData({});
      if (onReworkCreated) onReworkCreated();
    } catch (error) {
      console.error('Error updating request:', error);
      alert('Failed to update request');
    }
  };

  const handleDeleteRequest = async (requestId: number) => {
    setDeleteLoading(true);
    try {
      const enquiryId = enquiry.enquiry_id || enquiry.id;
      await api.delete(`/v2/enquiries/${enquiryId}/requests/${requestId}`);
      setShowDeleteConfirm(null);
      onClose();
      if (onReworkCreated) onReworkCreated();
    } catch (error: any) {
      const detail = error.response?.data?.detail || 'Failed to delete request';
      alert(detail);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${showDeleteConfirm.hasItineraries ? 'bg-red-100' : 'bg-yellow-100'}`}>
                  <AlertTriangle className={`h-5 w-5 ${showDeleteConfirm.hasItineraries ? 'text-red-600' : 'text-yellow-600'}`} />
                </div>
                <h2 className="text-lg font-bold text-gray-900">
                  {showDeleteConfirm.hasItineraries ? '⚠️ Admin Delete — Itineraries Will Be Archived' : 'Delete Request'}
                </h2>
              </div>

              {showDeleteConfirm.hasItineraries ? (
                <div className="space-y-3 mb-6">
                  <p className="text-gray-600">This request has linked itineraries that will be moved to <strong className="text-red-700">DELETED</strong> status:</p>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    {showDeleteConfirm.itineraryNames.map((name, i) => (
                      <div key={i} className="text-sm text-red-800 font-mono">• {name}</div>
                    ))}
                  </div>
                  <p className="text-sm text-gray-500">The itineraries will NOT be permanently deleted — they will be marked as DELETED and can be recovered by an admin if needed.</p>
                </div>
              ) : (
                <p className="text-gray-600 mb-6">Are you sure you want to delete this request? This action cannot be undone.</p>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  disabled={deleteLoading}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteRequest(showDeleteConfirm.requestId)}
                  disabled={deleteLoading}
                  className={`flex-1 text-white py-2 px-4 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 ${
                    showDeleteConfirm.hasItineraries ? 'bg-red-600 hover:bg-red-700' : 'bg-orange-600 hover:bg-orange-700'
                  }`}
                >
                  {deleteLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      {showDeleteConfirm.hasItineraries ? 'Delete & Archive Itineraries' : 'Delete Request'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-hidden">
        <div className="bg-white rounded-xl max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col">
          <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Enquiry Details - WN_{displayEnquiry.enquiry_id || displayEnquiry.id}{displayEnquiry.latest_request?.id ? `/ER_${displayEnquiry.latest_request.id}` : ''}</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-lg font-bold">WN_{enquiry.enquiry_id || enquiry.id}</span>
                  {displayEnquiry.latest_request?.id && (
                    <span className="font-mono text-sm text-gray-600">/ER_{displayEnquiry.latest_request.id}</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <span className={`px-2 py-1 text-xs font-semibold rounded uppercase ${getStatusColor(enquiry.status)}`}>
                    {getStatusLabel(enquiry.status)}
                  </span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded uppercase ${getPriorityColor(enquiry.priority)}`}>
                    {enquiry.priority}
                  </span>
                </div>
              </div>
              
              <div className="space-y-2">
                {enquiry.agency_name && (
                  <div className="text-sm"><span className="font-semibold">Agency:</span> {enquiry.agency_name}</div>
                )}
                {enquiry.customer_name && (
                  <div className="text-sm"><span className="font-semibold">Customer:</span> {enquiry.customer_name}</div>
                )}
                {enquiry.customer_reference && (
                  <div className="text-sm"><span className="font-semibold">Reference:</span> {enquiry.customer_reference}</div>
                )}
                {enquiry.pic_name && (
                  <div className="text-sm"><span className="font-semibold">PIC:</span> {enquiry.pic_name}</div>
                )}
              </div>
              
              {/* Assignment Info */}
              {(enquiry.status === 'assigned' || enquiry.status === 'in_progress' || enquiry.status === 'completed') && enquiry.assigned_user_name && (
                <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                        {enquiry.assigned_user_name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-blue-900 text-lg">{enquiry.assigned_user_name}</span>
                          <span className="px-2 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full">
                            ASSIGNED
                          </span>
                        </div>
                        {enquiry.assigned_at && (
                          <div className="flex items-center gap-1 text-sm text-blue-700 mt-1">
                            <span>📅</span>
                            <span>Assigned on {new Date(enquiry.assigned_at).toLocaleDateString('en-GB', { 
                              day: '2-digit', 
                              month: 'long', 
                              year: 'numeric' 
                            })}</span>
                            <span className="text-blue-500 ml-2">
                              ({Math.floor((new Date().getTime() - new Date(enquiry.assigned_at).getTime()) / (1000 * 60 * 60 * 24))} days ago)
                            </span>
                          </div>
                        )}
                        {enquiry.assigned_to && (
                          <div className="text-xs text-blue-600 mt-1">
                            ID: {enquiry.assigned_to}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-blue-600 mb-1">Status</div>
                      <div className={`px-2 py-1 text-xs font-semibold rounded uppercase ${getStatusColor(enquiry.status)}`}>
                        {getStatusLabel(enquiry.status)}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Travel Details */}
              {(enquiry.latest_request || enquiry.number_of_adults) && (
                <div className="flex items-center gap-3 text-sm text-gray-600 mt-3">
                  <span>📅 {new Date(enquiry.latest_request?.travel_date || enquiry.travel_date).toLocaleDateString('en-GB')}</span>
                  <span>•</span>
                  <span>👥 {enquiry.latest_request?.number_of_adults || enquiry.number_of_adults}A{(enquiry.latest_request?.number_of_kids || enquiry.number_of_kids) > 0 ? ` + ${enquiry.latest_request?.number_of_kids || enquiry.number_of_kids}K` : ''}</span>
                  {(enquiry.latest_request?.number_of_nights || enquiry.number_of_nights) && (
                    <>
                      <span>•</span>
                      <span>🌙 {enquiry.latest_request?.number_of_nights || enquiry.number_of_nights}N</span>
                    </>
                  )}
                </div>
              )}
              
              {/* Kids Information */}
              {((enquiry.latest_request && enquiry.latest_request.number_of_kids > 0) || (enquiry.number_of_kids > 0)) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                  <div className="text-sm font-medium text-blue-900 mb-1">
                    👶 Children: {enquiry.latest_request?.number_of_kids || enquiry.number_of_kids} kid{(enquiry.latest_request?.number_of_kids || enquiry.number_of_kids) > 1 ? 's' : ''}
                  </div>
                  <div className="text-sm text-blue-700">
                    Ages: {(() => {
                      // Try multiple sources for kids_ages
                      const kidsAges = displayEnquiry.kids_ages || enquiry.latest_request?.kids_ages || enquiry.kids_ages;
                      
                      if (kidsAges && Array.isArray(kidsAges) && kidsAges.length > 0) {
                        return kidsAges.map((age: number) => `${age} years`).join(', ');
                      } else if (kidsAges && typeof kidsAges === 'string') {
                        return kidsAges.replace(/[{}\"\\[\\]]/g, '').split(',').map((age: string) => `${parseInt(age.trim())} years`).join(', ');
                      }
                      return 'Not specified';
                    })()} 
                  </div>
                </div>
              )}
              
              {/* Customer Information Section */}
              {(enquiry.customer_name || enquiry.customer_reference || enquiry.pic_name) && (
                <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Customer Information</h4>
                  {enquiry.customer_name && (
                    <div className="text-sm text-gray-600 mb-1">
                      <span className="font-medium">👤 Customer:</span> {enquiry.customer_name}
                    </div>
                  )}
                  {enquiry.customer_reference && (
                    <div className="text-sm text-gray-600 mb-1">
                      <span className="font-medium">📋 Reference:</span> {enquiry.customer_reference}
                    </div>
                  )}
                  {enquiry.pic_name && (
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">📞 For more information please contact:</span> {enquiry.pic_name} from {enquiry.agency_name}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Request Details Section - Only show for reworks */}
            {displayEnquiry.plan_text && displayEnquiry.plan_text.trim() && displayEnquiry.request_history && displayEnquiry.request_history.some((req: any) => req.type === 'rework') && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold mb-4">Request Details</h2>
                <div className="bg-gray-50 border rounded p-4 text-sm whitespace-pre-wrap">
                  {displayEnquiry.plan_text}
                </div>
              </div>
            )}
            
            {/* Request History Timeline - Always show if available */}
            {displayEnquiry.request_history && displayEnquiry.request_history.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold mb-4">📜 Request Journey</h2>
                <div className="space-y-4">
                  {[...displayEnquiry.request_history].reverse().map((request: any) => (
                    <div key={request.id} className={`border-2 rounded-lg p-4 ${request.status !== 'completed' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{request.type === 'fresh' ? '🆕' : '🔄'}</span>
                          <h3 className="font-bold">{request.type === 'fresh' ? 'Original' : `Rework #${request.version}`}</h3>
                          <span className="font-mono text-xs text-gray-500">ER_{request.id}</span>
                          {request.status !== 'completed' && (
                            <span className="px-2 py-0.5 bg-blue-600 text-white text-xs font-semibold rounded-full">CURRENT</span>
                          )}
                          {(!isEditing || editingRequestId !== request.id) && (
                            <button
                              onClick={() => {
                                handleEditRequest(request);
                              }}
                              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1 ml-2"
                              title="Edit this request"
                              style={{ minWidth: '60px' }}
                            >
                              <Edit className="h-3 w-3" />
                              Edit
                            </button>
                          )}
                          {isEditing && editingRequestId === request.id && (
                            <div className="flex items-center gap-1 ml-2">
                              <button
                                onClick={handleSave}
                                className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center gap-1"
                                title="Save changes"
                              >
                                <Save className="h-3 w-3" />
                                Save
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="px-4 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700"
                                title="Cancel editing"
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                          {/* Delete button — per request, role-based */}
                          {(isAdmin || isTeamLead) && request.status !== 'completed' && !request.itineraries?.length && (
                            <button
                              onClick={() => setShowDeleteConfirm({ requestId: request.id, hasItineraries: false, itineraryNames: [] })}
                              className="px-3 py-2 bg-red-100 text-red-700 text-sm rounded-lg hover:bg-red-200 flex items-center gap-1 ml-2"
                              title="Delete this request (no itineraries attached)"
                            >
                              <Trash2 className="h-3 w-3" />
                              Delete
                            </button>
                          )}
                          {isAdmin && request.itineraries?.length > 0 && (
                            <button
                              onClick={() => setShowDeleteConfirm({
                                requestId: request.id,
                                hasItineraries: true,
                                itineraryNames: request.itineraries.map((i: any) => i.quotation_number ? `${i.quotation_number}_v${i.version_number}` : `WH_${i.id}`)
                              })}
                              className="px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 flex items-center gap-1 ml-2"
                              title="Admin: Delete request (itineraries will be marked as DELETED)"
                            >
                              <Shield className="h-3 w-3" />
                              <Trash2 className="h-3 w-3" />
                              Admin Delete
                            </button>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-500 mb-1">
                            📅 Created: {new Date(request.created_at + 'Z').toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </div>
                          <div className="text-xs text-gray-500">
                            🕒 {new Date(request.created_at + 'Z').toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                      
                      {request.base_itinerary_id && (
                        <div className="flex items-center p-2 bg-blue-100 border border-blue-200 rounded mb-2">
                          <div className="text-xs text-blue-700">
                            🎯 Based on: <strong>{request.base_quotation_number ? `${request.base_quotation_number}/${request.base_version_number} (WH_${request.base_itinerary_id})` : `WH_${request.base_itinerary_id}`}</strong>
                            {request.base_itinerary_name && ` — ${request.base_itinerary_name}`}
                          </div>
                        </div>
                      )}
                      
                      <div className="bg-white border rounded p-3 text-sm mb-2 whitespace-pre-wrap">
                        {request.status !== 'completed' && isEditing && editingRequestId === request.id ? (
                          <div className="space-y-3">
                            {agencies.length > 0 && request.type === 'fresh' && (
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Agency</label>
                                <SearchableSelect
                                  options={agencies.map(a => ({ value: a.agency_id, label: a.agency_name }))}
                                  value={editData.agency_id || ''}
                                  onChange={(value) => setEditData((prev: any) => ({ ...prev, agency_id: value }))}
                                  placeholder="SELECT AGENCY"
                                />
                              </div>
                            )}
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Travel Date</label>
                                <input
                                  type="date"
                                  value={editData.travel_date || ''}
                                  onChange={(e) => setEditData((prev: any) => ({ ...prev, travel_date: e.target.value }))}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Adults</label>
                                <input
                                  type="number"
                                  min="1"
                                  value={editData.number_of_adults || ''}
                                  onChange={(e) => setEditData((prev: any) => ({ ...prev, number_of_adults: parseInt(e.target.value) || 1 }))}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Request Text</label>
                              <textarea
                                rows={3}
                                value={editData.request_text || ''}
                                onChange={(e) => setEditData((prev: any) => ({ ...prev, request_text: e.target.value }))}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                            </div>
                          </div>
                        ) : (
                          request.change_reason || request.request_text
                        )}
                      </div>
                      
                      {/* Attachment Display */}
                      {request.attachment_file_path && request.attachment_file_name && (
                        <div className="mb-2">
                          <div className="flex items-center gap-2 p-2 bg-gray-50 rounded border">
                            <span className="text-xs">📄 {request.attachment_file_name}</span>
                            <div className="flex gap-1 ml-auto">
                              <a
                                href={request.attachment_file_path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                                title="View"
                              >
                                👁️
                              </a>
                              <a
                                href={request.attachment_file_path}
                                download={request.attachment_file_name}
                                className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                                title="Download"
                              >
                                📥
                              </a>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {request.itineraries && request.itineraries.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-2">📋 Itineraries</p>
                          <div className="space-y-1">
                            {request.itineraries.map((itin: any) => (
                              <div key={itin.id} className="bg-white border rounded p-2 flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2">
                                  {itin.quotation_number ? (
                                    <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-mono font-semibold">
                                      {itin.quotation_number}_v{itin.version_number}
                                    </span>
                                  ) : (
                                    <span className="bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded text-xs font-mono font-semibold">WH_{itin.id}</span>
                                  )}
                                  {itin.quotation_number && (
                                    <span className="text-[10px] text-gray-400">WH_{itin.id}</span>
                                  )}
                                  <span>{itin.name}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        // Try to download from itinerary_files first
                                        const response = await api.get(`/itineraries/${itin.id}/download/docx`);
                                        if (response.data.success && response.data.download_url) {
                                          window.open(response.data.download_url, '_blank');
                                        } else {
                                          throw new Error('No uploaded file found');
                                        }
                                      } catch (error) {
                                        console.error('Error downloading:', error);
                                        // Fallback to old download method
                                        try {
                                          await downloadItinerary(itin.id);
                                        } catch (fallbackError) {
                                          console.error('Fallback download failed:', fallbackError);
                                          alert('No uploaded file found for this itinerary');
                                        }
                                      }
                                    }}
                                    className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                                    title="Download"
                                  >
                                    📥
                                  </button>
                                  {enquiry.status === 'completed' && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedItinerary({id: itin.id, name: itin.name});
                                        setShowReworkModal(true);
                                      }}
                                      className="px-2 py-1 bg-orange-600 text-white rounded text-xs hover:bg-orange-700"
                                      title="Request Rework"
                                    >
                                      🔄
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Completion Notes */}
                      {request.completion_notes && (
                        <div className="mb-2 p-3 bg-green-50 border border-green-200 rounded">
                          <div className="text-xs font-semibold text-green-700 mb-1">📝 Completion Notes</div>
                          <div className="text-xs text-green-700 whitespace-pre-wrap">{request.completion_notes}</div>
                        </div>
                      )}
                      
                      {(!request.itineraries || request.itineraries.length === 0) && (
                        <p className="text-xs text-gray-500 italic">
                          {request.status === 'pending' ? '⏳ Awaiting itineraries...' : '📭 No itineraries created'}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex justify-end">
              <button onClick={onClose} className="bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Rework Modal */}
      {showReworkModal && selectedItinerary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Create Rework Request</h2>
              <button onClick={() => { setShowReworkModal(false); setSelectedItinerary(null); setReworkForm({ paxChanged: false, nightsChanged: false, dateChanged: false, changeReason: '', newAdults: '', newKids: '', newNights: '', newTravelDate: '', attachment: null }); }} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <div className="mb-6 p-3 bg-blue-50 rounded-lg border-2 border-blue-300">
                <p className="text-sm text-gray-600 mb-1">Reworking from:</p>
                <p className="font-bold text-blue-900 text-lg">WH_{selectedItinerary.id}</p>
                <p className="text-sm text-gray-700 mt-1">{selectedItinerary.name}</p>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-3">What has changed?</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input type="checkbox" checked={reworkForm.paxChanged} onChange={(e) => setReworkForm(prev => ({ ...prev, paxChanged: e.target.checked }))} className="mr-2 h-4 w-4 text-blue-600" />
                    <span className="text-gray-700">Pax</span>
                  </label>
                  <label className="flex items-center">
                    <input type="checkbox" checked={reworkForm.nightsChanged} onChange={(e) => setReworkForm(prev => ({ ...prev, nightsChanged: e.target.checked }))} className="mr-2 h-4 w-4 text-blue-600" />
                    <span className="text-gray-700">Nights</span>
                  </label>
                  <label className="flex items-center">
                    <input type="checkbox" checked={reworkForm.dateChanged} onChange={(e) => setReworkForm(prev => ({ ...prev, dateChanged: e.target.checked }))} className="mr-2 h-4 w-4 text-blue-600" />
                    <span className="text-gray-700">Dates</span>
                  </label>
                </div>
              </div>
              
              {reworkForm.paxChanged && (
                <div className="mb-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New Adults</label>
                    <input type="number" min="1" value={reworkForm.newAdults} onChange={(e) => setReworkForm(prev => ({ ...prev, newAdults: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New Kids</label>
                    <input type="number" min="0" value={reworkForm.newKids} onChange={(e) => setReworkForm(prev => ({ ...prev, newKids: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
              )}
              
              {reworkForm.nightsChanged && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Nights</label>
                  <input type="number" min="1" value={reworkForm.newNights} onChange={(e) => setReworkForm(prev => ({ ...prev, newNights: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
              )}
              
              {reworkForm.dateChanged && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Travel Date</label>
                  <input type="date" value={reworkForm.newTravelDate} onChange={(e) => setReworkForm(prev => ({ ...prev, newTravelDate: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
              )}
              
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Explain the change (required):</label>
                <textarea value={reworkForm.changeReason} onChange={(e) => setReworkForm(prev => ({ ...prev, changeReason: e.target.value }))} rows={4} placeholder="e.g., Client wants to add one more night in Ubud" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Attachment (Optional)</label>
                <input type="file" onChange={(e) => setReworkForm(prev => ({ ...prev, attachment: e.target.files?.[0] || null }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt" />
                {reworkForm.attachment && <p className="text-sm text-gray-600 mt-1">Selected: {reworkForm.attachment.name}</p>}
              </div>
            </div>
            
            <div className="border-t px-6 py-4 flex gap-3">
              <button onClick={() => { setShowReworkModal(false); setSelectedItinerary(null); setReworkForm({ paxChanged: false, nightsChanged: false, dateChanged: false, changeReason: '', newAdults: '', newKids: '', newNights: '', newTravelDate: '', attachment: null }); }} className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200">
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!reworkForm.changeReason.trim()) { alert('Please explain the change'); return; }
                  if (reworkForm.paxChanged && !reworkForm.newAdults) { alert('Please enter new number of adults'); return; }
                  if (reworkForm.nightsChanged && !reworkForm.newNights) { alert('Please enter new number of nights'); return; }
                  if (reworkForm.dateChanged && !reworkForm.newTravelDate) { alert('Please select new travel date'); return; }
                  
                  try {
                    const formData = new FormData();
                    formData.append('base_itinerary_id', selectedItinerary.id.toString());
                    formData.append('change_reason', reworkForm.changeReason);
                    formData.append('pax_changed', reworkForm.paxChanged.toString());
                    formData.append('date_changed', reworkForm.dateChanged.toString());
                    formData.append('nights_changed', reworkForm.nightsChanged.toString());
                    if (reworkForm.paxChanged && reworkForm.newAdults) {
                      formData.append('number_of_adults', reworkForm.newAdults);
                      formData.append('number_of_kids', reworkForm.newKids || '0');
                    }
                    if (reworkForm.nightsChanged && reworkForm.newNights) formData.append('number_of_nights', reworkForm.newNights);
                    if (reworkForm.dateChanged && reworkForm.newTravelDate) formData.append('travel_date', reworkForm.newTravelDate);
                    if (reworkForm.attachment) formData.append('attachment', reworkForm.attachment);
                    
                    await api.post(`/v2/enquiries/${enquiry.enquiry_id || enquiry.id}/rework`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                    alert('Rework request created successfully!');
                    setShowReworkModal(false);
                    setSelectedItinerary(null);
                    setReworkForm({ paxChanged: false, nightsChanged: false, dateChanged: false, changeReason: '', newAdults: '', newKids: '', newNights: '', newTravelDate: '', attachment: null });
                    if (onReworkCreated) onReworkCreated();
                  } catch (error) {
                    console.error('Error creating rework:', error);
                    alert('Failed to create rework request');
                  }
                }}
                className="flex-1 bg-orange-600 text-white py-2 px-4 rounded-lg hover:bg-orange-700 font-medium"
              >
                Create Rework Request
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EnquiryDetailsModal;